const STANDARD_FLOW_META = [
  {
    prefix: '/dashboard',
    section: 'Overview',
    title: 'Business Dashboard',
    summary: 'Track revenue, pending work, and customer activity in one glance.',
    tips: [
      'Check recent invoices before creating a new one.',
      'Use trends to spot payment delays early.',
    ],
    quickActions: [
      { label: 'Create Invoice', path: '/invoices/new' },
      { label: 'Add Customer', path: '/customers' },
    ],
    searchKeywords: ['dashboard', 'overview', 'revenue', 'today'],
  },
  {
    prefix: '/activity-history',
    section: 'Compliance',
    title: 'Activity History',
    summary: 'Review who changed invoices, customers, and tenant-facing settings across the workspace.',
    tips: [
      'Filter by record type and actor before exporting screenshots for reviews.',
      'Use admin surface filters to isolate tenant-facing platform actions.',
    ],
    quickActions: [
      { label: 'Dashboard', path: '/dashboard' },
      { label: 'Invoices', path: '/invoices' },
    ],
    searchKeywords: ['activity history', 'audit', 'compliance', 'changes'],
  },
  {
    prefix: '/invoices/new',
    section: 'Invoicing',
    title: 'Create Invoice',
    summary: 'Create a clean invoice quickly with accurate totals and taxes.',
    tips: [
      'Confirm customer and payment mode before saving.',
      'Verify tax selection to avoid billing corrections.',
    ],
    quickActions: [
      { label: 'Back to Invoices', path: '/invoices' },
      { label: 'Open Customers', path: '/customers' },
    ],
    searchKeywords: ['invoice', 'new invoice', 'bill'],
  },
  {
    prefix: '/invoices/edit',
    section: 'Invoicing',
    title: 'Edit Invoice',
    summary: 'Update invoice details while keeping customer records consistent.',
    tips: [
      'Review line-item prices before updating.',
      'Keep a note when payment mode changes.',
    ],
    quickActions: [
      { label: 'Invoice List', path: '/invoices' },
      { label: 'Sales Book', path: '/ledger' },
    ],
    searchKeywords: ['edit invoice', 'update invoice'],
  },
  {
    prefix: '/invoices/view',
    section: 'Invoicing',
    title: 'Invoice Detail',
    summary: 'Review complete invoice details before sharing or editing.',
    tips: [
      'Check totals and taxes before sending to customer.',
      'Use edit only when absolutely needed.',
    ],
    quickActions: [
      { label: 'All Invoices', path: '/invoices' },
      { label: 'Sales Book', path: '/ledger' },
    ],
    searchKeywords: ['invoice view', 'invoice detail'],
  },
  {
    prefix: '/invoices',
    section: 'Invoicing',
    title: 'Invoices',
    summary: 'Manage invoices, payment status, and customer billing history.',
    tips: [
      'Search by customer name or invoice number.',
      'Keep invoice dates and payment mode consistent.',
    ],
    quickActions: [
      { label: 'Create Invoice', path: '/invoices/new' },
      { label: 'Sales Book', path: '/ledger' },
    ],
    searchKeywords: ['invoices', 'billing', 'payments'],
  },
  {
    prefix: '/quotations/new',
    section: 'Quotations',
    title: 'Create Quotation',
    summary: 'Prepare a customer-ready quotation with clear item and pricing details.',
    tips: [
      'Select the right customer to avoid confusion later.',
      'Keep product unit and price aligned with final invoice plans.',
    ],
    quickActions: [
      { label: 'Quotation List', path: '/quotations' },
      { label: 'Products', path: '/products' },
    ],
    searchKeywords: ['new quotation', 'quote create'],
  },
  {
    prefix: '/quotations/edit',
    section: 'Quotations',
    title: 'Edit Quotation',
    summary: 'Refine quotation details while preserving customer context.',
    tips: [
      'Update item prices carefully before saving.',
      'Recheck customer details after edits.',
    ],
    quickActions: [
      { label: 'Quotation List', path: '/quotations' },
      { label: 'Customers', path: '/customers' },
    ],
    searchKeywords: ['edit quotation', 'update quote'],
  },
  {
    prefix: '/quotations/view',
    section: 'Quotations',
    title: 'Quotation Detail',
    summary: 'Review quotation before converting or sharing with customer.',
    tips: [
      'Double-check unit prices and item list.',
      'Use this view for final customer confirmation.',
    ],
    quickActions: [
      { label: 'All Quotations', path: '/quotations' },
      { label: 'Create New', path: '/quotations/new' },
    ],
    searchKeywords: ['quotation view', 'quote detail'],
  },
  {
    prefix: '/quotations',
    section: 'Quotations',
    title: 'Quotations',
    summary: 'Build and manage customer quotations with fast item lookup.',
    tips: [
      'Keep product rates updated to avoid quote rework.',
      'Link quotation updates to customer communication.',
    ],
    quickActions: [
      { label: 'Create Quotation', path: '/quotations/new' },
      { label: 'Products', path: '/products' },
    ],
    searchKeywords: ['quotations', 'quote'],
  },
  {
    prefix: '/estimated-invoices/new',
    section: 'Estimated Invoices',
    title: 'Create Estimated Invoice',
    summary: 'Draft expected billing values to align customer expectations early.',
    tips: [
      'Use realistic pricing and tax assumptions.',
      'Keep estimated and actual invoice references aligned.',
    ],
    quickActions: [
      { label: 'Estimated List', path: '/estimated-invoices' },
      { label: 'Create Invoice', path: '/invoices/new' },
    ],
    searchKeywords: ['estimated invoice', 'estimate create'],
  },
  {
    prefix: '/estimated-invoices/edit',
    section: 'Estimated Invoices',
    title: 'Edit Estimated Invoice',
    summary: 'Adjust estimated values while preserving customer trust.',
    tips: [
      'Track changes before final conversion to invoice.',
      'Confirm taxes after any item update.',
    ],
    quickActions: [
      { label: 'Estimated List', path: '/estimated-invoices' },
      { label: 'Quotations', path: '/quotations' },
    ],
    searchKeywords: ['estimate edit'],
  },
  {
    prefix: '/estimated-invoices/view',
    section: 'Estimated Invoices',
    title: 'Estimated Invoice Detail',
    summary: 'Review estimated totals before customer discussion.',
    tips: [
      'Validate all quantities and pricing before share.',
      'Use as a negotiation-ready draft.',
    ],
    quickActions: [
      { label: 'All Estimated', path: '/estimated-invoices' },
      { label: 'Create New', path: '/estimated-invoices/new' },
    ],
    searchKeywords: ['estimated view', 'estimate detail'],
  },
  {
    prefix: '/estimated-invoices',
    section: 'Estimated Invoices',
    title: 'Estimated Invoices',
    summary: 'Manage projected billing and prepare customers for final invoices.',
    tips: [
      'Use estimates when final quantity is uncertain.',
      'Convert to invoice quickly once confirmed.',
    ],
    quickActions: [
      { label: 'Create Estimated', path: '/estimated-invoices/new' },
      { label: 'Invoices', path: '/invoices' },
    ],
    searchKeywords: ['estimated invoices', 'projections'],
  },
  {
    prefix: '/products',
    section: 'Catalog',
    title: 'Products',
    summary: 'Maintain accurate product catalog for faster billing and quotations.',
    tips: [
      'Keep product names and units consistent.',
      'Update prices before creating documents.',
    ],
    quickActions: [
      { label: 'Invoices', path: '/invoices/new' },
      { label: 'Quotations', path: '/quotations/new' },
    ],
    searchKeywords: ['products', 'catalog', 'item master'],
  },
  {
    prefix: '/customers',
    section: 'CRM',
    title: 'Customers',
    summary: 'Keep customer details complete to avoid billing errors.',
    tips: [
      'Capture GST and contact details carefully.',
      'Use clean names for search-friendly records.',
    ],
    quickActions: [
      { label: 'Create Invoice', path: '/invoices/new' },
      { label: 'Create Quotation', path: '/quotations/new' },
    ],
    searchKeywords: ['customers', 'client', 'crm'],
  },
  {
    prefix: '/taxes',
    section: 'Tax Setup',
    title: 'Taxes',
    summary: 'Configure tax slabs once and reuse everywhere.',
    tips: [
      'Use clear tax names with rates.',
      'Avoid duplicate taxes with similar percentages.',
    ],
    quickActions: [
      { label: 'Invoices', path: '/invoices/new' },
      { label: 'Estimated Invoices', path: '/estimated-invoices/new' },
    ],
    searchKeywords: ['tax', 'gst', 'vat'],
  },
  {
    prefix: '/settings/admin',
    section: 'Administration',
    title: 'Administrative Settings',
    summary: 'Control workspace modes, shell layout, visibility, and interface defaults.',
    tips: [
      'Start with a workspace mode before overriding individual controls.',
      'Keep the default profile minimal unless a team workflow needs extra chrome.',
    ],
    quickActions: [
      { label: 'Company Settings', path: '/settings' },
      { label: 'Dashboard', path: '/dashboard' },
    ],
    searchKeywords: ['admin settings', 'workspace mode', 'theme', 'layout'],
  },
  {
    prefix: '/settings/fields',
    section: 'Administration',
    title: 'Field Controller',
    summary: 'Manage workspace field labels, placeholders, visibility, and ordering for shared forms.',
    tips: [
      'Only optional fields can be hidden.',
      'Use custom labels sparingly so shared forms stay familiar for the team.',
    ],
    quickActions: [
      { label: 'Company Settings', path: '/settings' },
      { label: 'Administrative Settings', path: '/settings/admin' },
    ],
    searchKeywords: ['field controller', 'fields', 'form governance', 'settings fields'],
  },
  {
    prefix: '/settings/custom-fields',
    section: 'Administration',
    title: 'Custom Fields',
    summary: 'Create tenant-specific fields for documents, customers, products, and taxes.',
    tips: [
      'Keep field types stable once records start using them.',
      'Use document placement settings only for invoice, estimate, and quotation fields.',
    ],
    quickActions: [
      { label: 'Field Controller', path: '/settings/fields' },
      { label: 'Form Layouts', path: '/document-form-layouts' },
    ],
    searchKeywords: ['custom fields', 'tenant fields', 'document custom fields', 'settings custom'],
  },
  {
    prefix: '/settings',
    section: 'Business Profile',
    title: 'Company Settings',
    summary: 'Configure business identity and invoice declaration details.',
    tips: [
      'Keep GSTIN and account details current.',
      'Review header text used in customer-facing documents.',
    ],
    quickActions: [
      { label: 'Administrative Settings', path: '/settings/admin' },
      { label: 'Dashboard', path: '/dashboard' },
    ],
    searchKeywords: ['settings', 'company profile', 'business details'],
  },
  {
    prefix: '/ledger',
    section: 'Reports',
    title: 'Sales Book',
    summary: 'Review historical sales for customer and compliance checks.',
    tips: [
      'Use date filters for faster audits.',
      'Check payment mode mix for cashflow planning.',
    ],
    quickActions: [
      { label: 'Invoices', path: '/invoices' },
      { label: 'Dashboard', path: '/dashboard' },
    ],
    searchKeywords: ['ledger', 'sales book', 'report'],
  },
];

function toKeyboardFlow(flow) {
  const prefix = flow.prefix === '/dashboard' ? '/keyboard' : `/keyboard${flow.prefix}`;
  return {
    ...flow,
    prefix,
    title: `${flow.title} (Keyboard Mode)`,
    section: `${flow.section} - Keyboard`,
  };
}

const KEYBOARD_FLOW_META = [
  ...STANDARD_FLOW_META.map(toKeyboardFlow),
  {
    ...toKeyboardFlow(STANDARD_FLOW_META[0]),
    prefix: '/keyboard/dashboard',
  },
];

const ALL_FLOW_META = [...STANDARD_FLOW_META, ...KEYBOARD_FLOW_META].sort(
  (a, b) => b.prefix.length - a.prefix.length
);

function matches(pathname, prefix) {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

export function getFlowMeta(pathname) {
  const hit = ALL_FLOW_META.find((flow) => matches(pathname, flow.prefix));
  if (hit) return hit;

  return {
    section: 'Workspace',
    title: 'Accounting Workspace',
    summary: 'Use the side navigation to continue your work.',
    tips: ['Open a module from navigation to get started.'],
    quickActions: [{ label: 'Go to Dashboard', path: '/dashboard' }],
    searchKeywords: ['workspace'],
  };
}

export function getFlowSearchIndex() {
  return STANDARD_FLOW_META.map((flow) => ({
    label: flow.title,
    path: flow.prefix,
    searchKeywords: flow.searchKeywords || [],
  }));
}
